import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Navbar from '../../components/Navbar';
import { Sunrise, Sun, Coffee, Moon, Clock, CheckCircle2, Calendar, Store } from 'lucide-react';
import { CustomPreorderPicker } from '../../components/CustomPreorderPicker';
import { loadRazorpayScript, openRazorpayCheckout } from '../../lib/razorpay';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MEAL_META = {
  breakfast: { icon: Sunrise, label: 'Breakfast', emoji: '🌅', color: 'amber' },
  lunch:     { icon: Sun, label: 'Lunch', emoji: '🍽️', color: 'orange' },
  snacks:    { icon: Coffee, label: 'Evening Snacks', emoji: '☕', color: 'rose' },
  dinner:    { icon: Moon, label: 'Dinner', emoji: '🌙', color: 'indigo' },
};

const MEAL_TYPE_LABELS = {
  veg_meal: { label: 'Veg Meal', emoji: '🟢' },
  non_veg_meal: { label: 'Non-Veg Meal', emoji: '🔴' },
  veg_salad: { label: 'Veg Salad', emoji: '🥗' },
  non_veg_salad: { label: 'Non-Veg Salad', emoji: '🥗' },
};
const MEAL_TYPE_ORDER = ['veg_meal', 'non_veg_meal', 'veg_salad', 'non_veg_salad'];

const Countdown = ({ to }) => {
  const [remaining, setRemaining] = useState(0);
  useEffect(() => {
    const tick = () => setRemaining(Math.max(0, new Date(to).getTime() - Date.now()));
    tick();
    const i = setInterval(tick, 30000);
    return () => clearInterval(i);
  }, [to]);
  if (remaining <= 0) return null;
  const hours = Math.floor(remaining / 3600000);
  const mins = Math.floor((remaining % 3600000) / 60000);
  return <span className="text-text-muted">Cutoff in {hours}h {mins}m</span>;
};

const ReservationCard = ({ meal, onReserve, onReserveCustom, onCancel, reserving }) => {
  const meta = MEAL_META[meal.meal_period];
  const Icon = meta.icon;
  const isReserved = !!meal.already_reserved;
  const isDisabled = !meal.enabled;
  const cutoffPassed = meal.cutoff_passed;
  const lockedByOther = !!meal.locked_by_other;
  const lockedByMeal = meal.locked_by_meal;
  const [selectedVendorId, setSelectedVendorId] = useState(meal.eligible_vendors?.[0]?.id || '');
  const [mealType, setMealType] = useState('veg_meal');

  const canReserve = !isReserved && !isDisabled && !cutoffPassed && !lockedByOther && meal.eligible_vendors?.length > 0;
  const isCustomVendor = meal.eligible_vendors?.find((v) => v.id === selectedVendorId)?.preorder_mode === 'custom';
  const reserved = meal.already_reserved;

  return (
    <div
      data-testid={`reservation-card-${meal.meal_period}`}
      className={`bg-card border rounded-2xl p-5 transition-all ${
        isReserved ? 'border-green-300 bg-green-50/30' :
        isDisabled || cutoffPassed || lockedByOther ? 'border-border-light opacity-60' :
        'border-border-light hover:shadow-md'
      }`}
    >
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-3">
          <div className={`bg-primary-light rounded-xl p-2.5`}>
            <Icon className="h-5 w-5 text-primary" />
          </div>
          <div>
            <h3 className="font-heading text-lg font-semibold text-text-primary">
              {meta.emoji} {meta.label}
            </h3>
            <p className="text-xs text-text-muted">Tomorrow • {meal.delivery_date}</p>
          </div>
        </div>
        {isReserved && (
          <span data-testid={`status-reserved-${meal.meal_period}`} className="bg-green-100 text-green-700 text-xs font-medium px-2.5 py-1 rounded-full flex items-center gap-1">
            <CheckCircle2 className="h-3 w-3" /> Reserved
          </span>
        )}
        {isDisabled && (
          <span data-testid={`status-disabled-${meal.meal_period}`} className="bg-gray-100 text-gray-600 text-xs font-medium px-2.5 py-1 rounded-full">Disabled by admin</span>
        )}
        {!isDisabled && !isReserved && cutoffPassed && (
          <span data-testid={`status-cutoff-${meal.meal_period}`} className="bg-red-50 text-red-700 text-xs font-medium px-2.5 py-1 rounded-full flex items-center gap-1">
            <Clock className="h-3 w-3" /> Cutoff passed
          </span>
        )}
        {!isDisabled && !isReserved && !cutoffPassed && lockedByOther && (
          <span data-testid={`status-locked-${meal.meal_period}`} className="bg-amber-50 text-amber-700 text-xs font-medium px-2.5 py-1 rounded-full flex items-center gap-1">
            🔒 {lockedByMeal} already booked
          </span>
        )}
      </div>

      {lockedByOther && !isReserved && (
        <p className="text-xs text-text-muted bg-amber-50 border border-amber-200 rounded-lg p-3" data-testid={`locked-msg-${meal.meal_period}`}>
          You can book only <strong>one meal per day</strong>. Cancel your <strong>{lockedByMeal}</strong> reservation to switch to {meta.label.toLowerCase()}.
        </p>
      )}
      {!lockedByOther && (
        <>
          {isReserved ? (
            <>
              <div className="bg-white border border-green-200 rounded-lg p-3 text-sm">
                <p className="text-xs text-text-muted">Reserved with</p>
                <p className="font-medium text-text-primary flex items-center gap-1.5 mt-1">
                  <Store className="h-4 w-4" /> {reserved.vendor_name || 'Vendor'}
                </p>
                {reserved.meal_type_label && (
                  <p data-testid={`reserved-items-${meal.meal_period}`} className="text-xs text-text-secondary mt-1.5 break-words">{reserved.meal_type_label}</p>
                )}
                {reserved.total_amount != null && (
                  <p data-testid={`reserved-total-${meal.meal_period}`} className="text-xs mt-1 flex items-center gap-2">
                    <span className="font-semibold text-text-primary">₹{Number(reserved.total_amount).toFixed(0)}</span>
                    <span className={`px-2 py-0.5 rounded-full ${reserved.payment_status === 'paid' ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}`}>
                      {reserved.payment_status === 'paid' ? 'Paid online' : 'Pay at counter'}
                    </span>
                  </p>
                )}
              </div>
              <button
                onClick={() => onCancel(meal.already_reserved.id)}
                data-testid={`cancel-reservation-${meal.meal_period}`}
                className="mt-3 w-full text-sm text-red-600 hover:bg-red-50 py-2 rounded-lg border border-red-200 transition-colors"
              >
                Cancel reservation
              </button>
            </>
          ) : (
            <>
              {meal.eligible_vendors?.length > 0 ? (
                <>
                  <label className="block text-xs text-text-muted mb-1.5">Vendor</label>
                  <select
                    value={selectedVendorId}
                    onChange={(e) => setSelectedVendorId(e.target.value)}
                    disabled={!canReserve}
                    data-testid={`vendor-select-${meal.meal_period}`}
                    className="w-full px-3 py-2 border border-border-light rounded-lg bg-background text-sm focus:ring-2 focus:ring-primary/20 focus:border-primary"
                  >
                    {meal.eligible_vendors.map(v => <option key={v.id} value={v.id}>{v.name}</option>)}
                  </select>
                  {isCustomVendor ? (
                    <CustomPreorderPicker
                      vendorId={selectedVendorId}
                      mealPeriod={meal.meal_period}
                      disabled={!canReserve}
                      busy={reserving}
                      onSubmit={(lines) => onReserveCustom(meal.meal_period, selectedVendorId, lines)}
                    />
                  ) : (<>
                  <label className="block text-xs text-text-muted mb-1.5 mt-3">Meal type</label>
                  <div className="flex flex-wrap gap-2">
                    {MEAL_TYPE_ORDER.map((mt) => {
                      const isActive = mealType === mt;
                      return (
                        <button
                          type="button"
                          key={mt}
                          disabled={!canReserve}
                          onClick={() => setMealType(mt)}
                          data-testid={`meal-type-${meal.meal_period}-${mt}`}
                          className={`px-3 py-1.5 text-xs font-medium rounded-full border transition-colors ${
                            isActive
                              ? 'bg-primary text-white border-primary'
                              : 'bg-background text-text-secondary border-border-light hover:border-primary'
                          } disabled:opacity-50 disabled:cursor-not-allowed`}
                        >
                          {MEAL_TYPE_LABELS[mt].emoji} {MEAL_TYPE_LABELS[mt].label}
                        </button>
                      );
                    })}
                  </div>
                  <button
                    onClick={() => onReserve(meal.meal_period, selectedVendorId, mealType)}
                    disabled={!canReserve || reserving}
                    data-testid={`reserve-btn-${meal.meal_period}`}
                    className="mt-3 w-full bg-primary hover:bg-primary-hover text-white font-medium py-2.5 rounded-lg disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                  >
                    {reserving ? 'Reserving...' : 'Reserve'}
                  </button>
                  </>)}
                </>
              ) : (
                <p className="text-sm text-text-muted py-2">No vendors available for this meal yet.</p>
              )}
              {!cutoffPassed && !isDisabled && (
                <p className="text-xs text-text-muted mt-2 text-center">
                  <Clock className="h-3 w-3 inline mr-1" /><Countdown to={meal.cutoff_at} />
                </p>
              )}
            </>
          )}
        </>
      )}
    </div>
  );
};

const EmployeeReservations = () => {
  const [availability, setAvailability] = useState(null);
  const [loading, setLoading] = useState(true);
  const [reserving, setReserving] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  const fetchAvailability = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/reservations/availability`, { withCredentials: true });
      setAvailability(data);
    } catch (e) {
      setError(e.response?.data?.detail || 'Could not load reservations');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchAvailability(); }, []);

  const handleReserve = async (mealPeriod, vendorId, mealType) => {
    setReserving(true);
    setError(''); setMessage('');
    try {
      await axios.post(`${API}/reservations`, { vendor_id: vendorId, meal_period: mealPeriod, meal_type: mealType }, { withCredentials: true });
      setMessage(`✅ ${MEAL_TYPE_LABELS[mealType]?.label || ''} ${MEAL_META[mealPeriod].label} reserved for tomorrow`);
      setTimeout(() => setMessage(''), 4000);
      fetchAvailability();
    } catch (e) {
      setError(e.response?.data?.detail || 'Reservation failed');
    } finally {
      setReserving(false);
    }
  };

  const handleReserveCustom = async (mealPeriod, vendorId, lines) => {
    setReserving(true);
    setError(''); setMessage('');
    try {
      const { data: co } = await axios.post(`${API}/reservations/custom/checkout`,
        { vendor_id: vendorId, meal_period: mealPeriod, items: lines }, { withCredentials: true });
      let reservation = co.reservation;
      if (co.payment_required) {
        let pay = { razorpay_order_id: co.razorpay_order_id, razorpay_payment_id: `pay_mock_${Date.now()}`, razorpay_signature: '' };
        if (!co.mock_mode) {
          if (!(await loadRazorpayScript())) throw new Error('Could not load the payment gateway. Check your internet and try again.');
          pay = await openRazorpayCheckout({
            keyId: co.key_id, razorpayOrderId: co.razorpay_order_id, amount: co.amount, currency: co.currency,
            description: `Pre-order — ${co.vendor_name}`,
          });
        }
        const { data: v } = await axios.post(`${API}/reservations/custom/verify`, pay, { withCredentials: true });
        reservation = v.reservation;
      }
      setMessage(`✅ ${MEAL_META[mealPeriod].label} pre-ordered for tomorrow · ₹${Number(reservation.total_amount).toFixed(0)} ${reservation.payment_status === 'paid' ? 'paid' : 'to pay at counter'}`);
      setTimeout(() => setMessage(''), 5000);
      fetchAvailability();
    } catch (e) {
      setError(e.response?.data?.detail || e.message || 'Pre-order failed');
    } finally {
      setReserving(false);
    }
  };

  const handleCancel = async (reservationId) => {
    if (!window.confirm('Cancel this reservation? Paid pre-orders are refunded to your original payment method.')) return;
    setError('');
    try {
      const { data } = await axios.delete(`${API}/reservations/${reservationId}`, { withCredentials: true });
      setMessage(data?.refund_status ? 'Reservation cancelled — refund initiated' : 'Reservation cancelled');
      setTimeout(() => setMessage(''), 3000);
      fetchAvailability();
    } catch (e) {
      setError(e.response?.data?.detail || 'Cancel failed');
    }
  };

  if (loading) {
    return (
      <>
        <Navbar />
        <div className="min-h-screen bg-background flex items-center justify-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      </>
    );
  }

  return (
    <>
      <Navbar />
      <div className="min-h-screen bg-background">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
          <div className="mb-8">
            <h1 className="font-heading text-3xl sm:text-4xl md:text-5xl tracking-tighter font-semibold text-text-primary">
              Reserve Tomorrow's Meals
            </h1>
            <div className="flex items-center gap-2 mt-2 text-text-secondary">
              <Calendar className="h-4 w-4" />
              <span data-testid="reservation-date">Reservations for {availability?.date}</span>
            </div>
          </div>

          <div className="bg-primary-light border border-primary/20 rounded-2xl p-4 mb-6 flex items-start gap-3">
            <Clock className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
            <div className="text-sm text-text-secondary">
              <strong className="text-text-primary">How it works:</strong> Pick one meal per slot for tomorrow.
              Reservations close at <strong>8 PM today</strong> (your site admin can change this).
              Standard meals are a free head-count; vendors with their own pre-order menu show items with prices — pay online to confirm.
            </div>
          </div>

          {message && (
            <div data-testid="reservation-success" className="mb-4 p-4 bg-green-50 border border-green-200 rounded-lg text-green-700 text-sm">{message}</div>
          )}
          {error && (
            <div data-testid="reservation-error" className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">{error}</div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {availability?.meals?.map((m) => (
              <ReservationCard
                key={m.meal_period}
                meal={m}
                onReserve={handleReserve}
                onReserveCustom={handleReserveCustom}
                onCancel={handleCancel}
                reserving={reserving}
              />
            ))}
          </div>
        </div>
      </div>
    </>
  );
};

export default EmployeeReservations;
